import { createContext, useContext, useEffect, useMemo } from "react";
import {
  AuthenticatedArea,
  AuthPortal,
  SuiteAccountMenuControls,
  useAccessToken,
  useRememberSuiteProduct,
  useSuiteAccountAvatar,
  useTribunaleaAccess,
} from "@tribunalea/auth/react";
import {
  APPLICATION_IDS,
  canAccessApplication,
  canAccessTool,
  classifyExpedienteIdentifier,
  createFullSuiteAccess,
  TOOL_IDS,
} from "@tribunalea/shared";
import { Justiniano } from "@tribunalea/justiniano";
import {
  InstitutionalAccessPage,
  useActivityTracking,
  useRouter,
} from "@tribunalea/ui";
import { AppShell } from "./components/AppShell.jsx";
import { createApi } from "./lib/api.js";
import { DashboardPage } from "./pages/DashboardPage.jsx";
import { NewRunPage } from "./pages/NewRunPage.jsx";
import { RunDetailPage } from "./pages/RunDetailPage.jsx";
import { TaxIdentitySearchPage } from "./pages/TaxIdentitySearchPage.jsx";
import { ProfessionalSearchPage } from "./pages/ProfessionalSearchPage.jsx";
import { SentenceSearchPage } from "./pages/SentenceSearchPage.jsx";
import { TemplateEditorPage } from "./pages/TemplateEditorPage.jsx";
import { ToolsPage } from "./pages/ToolsPage.jsx";
import { LibraryAiPage } from "./pages/LibraryAiPage.jsx";
import { LibraryPage } from "./pages/LibraryPage.jsx";
import { NotebookPage } from "./pages/NotebookPage.jsx";
import { JudicialInterestCalculatorPage } from "./pages/JudicialInterestCalculatorPage.jsx";
import { JurisprudenceSearchPage } from "./pages/JurisprudenceSearchPage.jsx";
import { JurisprudenceDetailPage } from "./pages/JurisprudenceDetailPage.jsx";
import { DoctrineCatalogPage } from "./pages/DoctrineCatalogPage.jsx";
import { DoctrineDetailPage } from "./pages/DoctrineDetailPage.jsx";
import { NormCatalogPage } from "./pages/NormCatalogPage.jsx";
import { NormDetailPage } from "./pages/NormDetailPage.jsx";
import { ExpedienteSearchPage } from "./pages/ExpedienteSearchPage.jsx";
import { ExpedienteDetailPage } from "./pages/ExpedienteDetailPage.jsx";

const ApiContext = createContext(null);
export const useApi = () => useContext(ApiContext);

const accountCreditsUrl = `${import.meta.env.VITE_ACCOUNT_URL || (
  import.meta.env.DEV
    ? "http://localhost:5175"
    : "https://account.tribunalea.com"
)}/creditos`;
const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function instrumentaToolForPath(path) {
  if (/^\/expedientes(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_EXPEDIENTES;
  }
  if (/^\/(?:procesador-documental|procesar|plantillas)(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_DOCUMENT_PROCESSOR;
  }
  if (/^\/calculadora-intereses(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_JUDICIAL_INTEREST;
  }
  if (/^\/consulta-fiscal(?:\/|$)/.test(path)) return TOOL_IDS.INSTRUMENTA_TAX_ID;
  if (/^\/matriculas-profesionales(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_PROFESSIONALS;
  }
  if (/^\/buscador-sentencias(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_SENTENCES;
  }
  if (/^\/(?:buscador-jurisprudencia|jurisprudencia)(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_JURISPRUDENCE;
  }
  if (/^\/doctrina(?:\/|$)/.test(path)) return TOOL_IDS.INSTRUMENTA_DOCTRINE;
  if (/^\/normas(?:\/|$)/.test(path)) return TOOL_IDS.INSTRUMENTA_NORMS;
  if (/^\/(?:biblioteca|justiniano)(?:\/|$)/.test(path)) {
    return TOOL_IDS.INSTRUMENTA_LIBRARY;
  }
  return null;
}

function AccountRedirect() {
  useEffect(() => {
    window.location.replace(accountCreditsUrl);
  }, []);
  return <main className="embedded-shell">Abriendo Cuenta…</main>;
}

function RouteRedirect({ to }) {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return <main className="route-loading">Abriendo…</main>;
}

function ProductRoutes({
  access,
  accessError = null,
  accessLoading = false,
  accountAvatar,
  authEnabled,
  clerkControls,
  getAccessToken,
  reloadAccess = null,
}) {
  const { path, search, navigate } = useRouter();
  const embedded = useMemo(() => (
    window.self !== window.top
    || new URLSearchParams(window.location.search).get("embed") === "1"
  ), []);
  const toolId = instrumentaToolForPath(path);
  const accountRoute = path === "/cuenta/creditos" || path === "/uso";
  const applicationAllowed = canAccessApplication(
    access,
    APPLICATION_IDS.INSTRUMENTA,
  );
  const routeAllowed = accountRoute || (
    applicationAllowed && (!toolId || canAccessTool(access, toolId))
  );
  useActivityTracking({
    apiUrl,
    getAccessToken,
    appId: APPLICATION_IDS.INSTRUMENTA,
    toolId: routeAllowed ? toolId : null,
    enabled: authEnabled
      && !embedded
      && !accessLoading
      && !accessError
      && routeAllowed,
    scopeKey: access?.workspace?.id || "personal",
  });
  if (accessLoading || accessError) {
    return (
      <InstitutionalAccessPage
        error={accessError}
        loading={accessLoading}
        onRetry={reloadAccess}
      />
    );
  }
  const templateManagementAllowed = !access?.workspace?.institutional
    || access.workspace.role === "org:admin";
  const expedienteRouteMatch = /^\/expedientes\/([^/]+)(?:\/(?:informacion|actuaciones|documentos))?\/?$/.exec(
    path,
  );
  const activeExpediente = expedienteRouteMatch
    ? classifyExpedienteIdentifier(decodeURIComponent(expedienteRouteMatch[1]))
    : null;
  let page;
  if (!routeAllowed) {
    page = <InstitutionalAccessPage label={toolId ? "Esta herramienta" : "Instrumenta"} />;
  }
  else if (path === "/") page = <ToolsPage access={access} />;
  else if (path === "/expedientes" || path === "/expedientes/") {
    page = <ExpedienteSearchPage />;
  } else if (activeExpediente) {
    page = (
      <ExpedienteDetailPage
        identifier={activeExpediente.identifier}
        sourceId={activeExpediente.sourceId}
      />
    );
  }
  else if (
    path === "/procesador-documental" ||
    path === "/procesador-documental/"
  ) {
    page = <DashboardPage access={access} view="history" />;
  } else if (
    path === "/procesador-documental/plantillas" ||
    path === "/procesador-documental/plantillas/" ||
    path === "/plantillas" ||
    path === "/plantillas/"
  ) {
    page = <DashboardPage access={access} view="templates" />;
  } else if (
    path === "/procesador-documental/procesar" ||
    path === "/procesar"
  ) {
    page = <NewRunPage />;
  } else if (
    path === "/procesador-documental/plantillas/nueva" ||
    path === "/plantillas/nueva"
  ) {
    page = templateManagementAllowed
      ? <TemplateEditorPage />
      : <InstitutionalAccessPage label="La administración de plantillas" />;
  } else if (
    /^\/(?:procesador-documental\/)?plantillas\/[a-f\d]{24}$/i.test(path)
  ) {
    page = templateManagementAllowed
      ? <TemplateEditorPage templateId={path.split("/").at(-1)} />
      : <InstitutionalAccessPage label="La administración de plantillas" />;
  } else if (
    /^\/(?:procesador-documental\/)?procesamientos\/[a-f\d]{24}$/i.test(path)
  ) {
    page = <RunDetailPage runId={path.split("/").at(-1)} />;
  } else if (path === "/consulta-fiscal") {
    page = <TaxIdentitySearchPage />;
  } else if (path === "/matriculas-profesionales") {
    page = <ProfessionalSearchPage />;
  } else if (path === "/buscador-sentencias") {
    page = <SentenceSearchPage />;
  } else if (path === "/buscador-jurisprudencia" || path === "/buscador-jurisprudencia/") {
    page = <RouteRedirect to={`/jurisprudencia${search}`} />;
  } else if (path === "/jurisprudencia" || path === "/jurisprudencia/") {
    page = <JurisprudenceSearchPage />;
  } else if (/^\/jurisprudencia\/[^/]+\/?$/i.test(path)) {
    page = <JurisprudenceDetailPage slug={path.split("/").filter(Boolean).at(-1)} />;
  } else if (path === "/doctrina" || path === "/doctrina/") {
    page = <DoctrineCatalogPage />;
  } else if (/^\/doctrina\/[^/]+\/pagina\/\d+\/?$/i.test(path)) {
    const segments = path.split("/").filter(Boolean);
    page = <DoctrineDetailPage slug={segments[1]} pageNumber={Number(segments[3])} />;
  } else if (/^\/doctrina\/[^/]+\/?$/i.test(path)) {
    page = <DoctrineDetailPage slug={path.split("/").filter(Boolean).at(-1)} />;
  } else if (path === "/normas" || path === "/normas/") {
    page = <NormCatalogPage />;
  } else if (/^\/normas\/[^/]+\/pagina\/\d+\/?$/i.test(path)) {
    const segments = path.split("/").filter(Boolean);
    page = <NormDetailPage slug={segments[1]} pageNumber={Number(segments[3])} />;
  } else if (
    /^\/normas\/[^/]+\/(?:parte|libro|titulo|capitulo|seccion|articulo|inciso|parrafo|anexo|unidad)\/[^/]+\/?$/i.test(path)
  ) {
    const segments = path.split("/").filter(Boolean);
    page = <NormDetailPage slug={segments[1]} unitSlug={segments[3]} />;
  } else if (/^\/normas\/[^/]+\/?$/i.test(path)) {
    page = <NormDetailPage slug={path.split("/").filter(Boolean).at(-1)} />;
  } else if (
    path === "/calculadora-intereses"
    || path === "/calculadora-intereses/"
  ) {
    page = <JudicialInterestCalculatorPage view="history" />;
  } else if (
    path === "/calculadora-intereses/nuevo"
    || path === "/calculadora-intereses/nuevo/"
  ) {
    page = <JudicialInterestCalculatorPage view="choose" />;
  } else if (
    path === "/calculadora-intereses/nuevo/manual"
    || path === "/calculadora-intereses/nuevo/manual/"
  ) {
    page = <JudicialInterestCalculatorPage view="manual" />;
  } else if (
    path === "/calculadora-intereses/nuevo/sentencia"
    || path === "/calculadora-intereses/nuevo/sentencia/"
  ) {
    page = <JudicialInterestCalculatorPage view="sentences" />;
  } else if (/^\/calculadora-intereses\/[a-f\d]{24}\/(?:rubros|resultado)\/?$/i.test(path)) {
    const segments = path.split("/").filter(Boolean);
    page = (
      <JudicialInterestCalculatorPage
        calculationId={segments[1]}
        sentenceStep={segments[2]}
      />
    );
  } else if (/^\/calculadora-intereses\/[a-f\d]{24}\/?$/i.test(path)) {
    page = (
      <JudicialInterestCalculatorPage
        calculationId={path.split("/").filter(Boolean).at(-1)}
      />
    );
  } else if (
    path === "/biblioteca"
    || path === "/justiniano"
  ) {
    page = <LibraryAiPage getAccessToken={getAccessToken} />;
  } else if (path === "/biblioteca/administrar") {
    page = <LibraryPage management />;
  } else if (
    /^\/biblioteca\/administrar\/[a-f\d]{24}$/i.test(path)
  ) {
    page = (
      <NotebookPage
        management
        notebookId={path.split("/").at(-1)}
      />
    );
  } else if (/^\/biblioteca\/[a-f\d]{24}$/i.test(path)) {
    page = (
      <LibraryAiPage
        getAccessToken={getAccessToken}
        initialNotebookId={path.split("/").at(-1)}
      />
    );
  } else if (path === "/cuenta/creditos" || path === "/uso") {
    page = <AccountRedirect />;
  } else {
    page = <RouteRedirect to="/" />;
  }

  if (embedded) {
    return <main className="embedded-shell">{page}</main>;
  }
  const hasFullPageAssistant =
    path === "/biblioteca"
    || path === "/justiniano"
    || /^\/biblioteca\/[a-f\d]{24}$/i.test(path);
  return (
    <>
      <AppShell
        access={access}
        accountAvatar={accountAvatar}
        authEnabled={authEnabled}
        clerkControls={clerkControls}
      >
        {page}
      </AppShell>
      {routeAllowed
        && !hasFullPageAssistant
        && canAccessTool(access, TOOL_IDS.INSTRUMENTA_ASSISTANT) && (
        <Justiniano
          allowAttachments
          enablePageHelp
          appId="instrumenta"
          appName="Instrumenta"
          apiUrl={apiUrl}
          getAccessToken={getAccessToken}
          onNavigate={navigate}
          welcome={path.startsWith("/jurisprudencia")
            ? "Puedo analizar un fallo o comparar los que selecciones, siempre mostrando las fuentes utilizadas."
            : path.startsWith("/doctrina")
              ? "Puedo ayudarte a ubicar una obra, explicar sus argumentos y trabajar con pasajes verificables."
              : path.startsWith("/normas")
                ? "Puedo ayudarte a encontrar una norma, revisar su vigencia y explicar sus artículos con fuente."
                : "Puedo explicarte cómo usar esta pantalla y ayudarte con consultas jurídicas cuando corresponda."}
          suggestions={path.startsWith("/jurisprudencia") ? [
            "¿Cómo elijo buenos fallos para comparar?",
            "¿Qué diferencia hay entre coincidencia textual y conceptual?",
            "¿Cómo verifico el criterio de una sentencia?",
          ] : path.startsWith("/doctrina") ? [
            "¿Cómo busco por autor o título?",
            "¿Cómo verifico un pasaje doctrinario?",
            "¿Podés comparar dos posturas doctrinarias?",
          ] : path.startsWith("/normas") ? [
            "¿Cómo consulto el texto vigente a una fecha?",
            "¿Cómo busco una norma por su contenido?",
            "¿Podés explicarme un artículo?",
          ] : [
            "¿Qué puedo hacer en esta pantalla?",
            "¿Cómo empiezo?",
            "¿Cómo funciona internamente?",
          ]}
          appContext={{
            route: path,
            interfaceHelp: true,
          }}
        />
      )}
    </>
  );
}

function ClerkApp() {
  const getToken = useAccessToken();
  const accountAvatar = useSuiteAccountAvatar();
  const api = useMemo(() => createApi(getToken), [getToken]);
  const accessState = useTribunaleaAccess({ apiUrl });
  useRememberSuiteProduct("instrumenta", {
    enabled: window.self === window.top
      && Boolean(accessState.access)
      && canAccessApplication(
        accessState.access,
        APPLICATION_IDS.INSTRUMENTA,
      ),
  });
  return (
    <ApiContext.Provider value={api}>
      <ProductRoutes
        access={accessState.access}
        accessError={accessState.error}
        accessLoading={accessState.loading}
        accountAvatar={accountAvatar}
        authEnabled
        clerkControls={<SuiteAccountMenuControls />}
        getAccessToken={getToken}
        reloadAccess={accessState.reload}
      />
    </ApiContext.Provider>
  );
}

function SignInScreen() {
  return (
    <AuthPortal
      productName="INSTRUMENTA"
      headline="Herramientas concretas para trabajar con información."
      description="Procesá documentos, consultá datos y convertí información dispersa en resultados verificables."
      proofPoints={[
        "Procesamiento documental",
        "Consultas con fuentes",
        "Resultados reutilizables",
      ]}
    />
  );
}

export default function App({ authEnabled }) {
  if (authEnabled) {
    return (
      <AuthenticatedArea signedOut={<SignInScreen />}>
        <ClerkApp />
      </AuthenticatedArea>
    );
  }
  const api = createApi(null);
  const access = createFullSuiteAccess();
  return (
    <ApiContext.Provider value={api}>
      <ProductRoutes
        access={access}
        authEnabled={false}
        getAccessToken={null}
      />
    </ApiContext.Provider>
  );
}
